import {useCallback} from 'react';

import {CTokenDecoded, TypeVerification} from '@/app/auth';
import {VERIFY_EVENTS} from '@/app/auth/events/VerifyEvents';
import {C_ORIGIN, PROJECT_TAGS, VERIFY_CODE_FROM_PAGE} from '@/constants';
import {getTokenDecoded, useTrackingManager} from '@/hooks';
import {
  IEventBaseDataSchema,
  IEventIdentifyDataDefinition,
  OriginType,
} from '@/services';

const SECOND = 1000;
const GOAL_WITH_MODAK_STORAGE_KEY = 'GOAL_WITH_MODAK';

/** Error shape covers both raw JS errors and Amplify/Cognito error objects. */
type TrackedError = {code?: string; name?: string; message?: string};

// Shared payload builders — pulled out of the hook since they're pure
// transforms with no dependency on tracking-manager state, and were
// previously duplicated verbatim across two callbacks each.
const buildOtpViewEventData = (
  isConfirmSignUp: boolean,
  referralCode?: string,
  invitationCode?: string,
): IEventBaseDataSchema => ({
  project_tag: PROJECT_TAGS.ACCOUNT_ACCESS,
  origin: isConfirmSignUp ? C_ORIGIN.SIGNUP : C_ORIGIN.SIGNIN,
  Invitation: {
    code: invitationCode,
    written: false,
  },
  user: {
    referral: {
      code: referralCode,
    },
    typeVerification: TypeVerification.SMS,
  },
});

const buildOtpResultEventData = (
  isFromSignIn: boolean,
  invitationCode?: string,
  referralCode?: string,
): IEventBaseDataSchema => ({
  origin: isFromSignIn
    ? VERIFY_CODE_FROM_PAGE.SIGNIN
    : VERIFY_CODE_FROM_PAGE.SIGNUP,
  Invitation: {
    code: invitationCode,
    written: false,
  },
  user: {
    typeVerification: TypeVerification.SMS,
    referral: {
      code: referralCode?.toUpperCase(),
    },
  },
  project_tag: PROJECT_TAGS.ACCOUNT_ACCESS,
});

const buildErrorPayload = (err: TrackedError) => ({
  code: err?.code || err?.name,
  message: err?.message,
});

export const useTrackers = () => {
  const {trackEvent, trackIdentifyEvents} = useTrackingManager();

  const trackViewAuthEnterOTP = useCallback(
    (
      isConfirmSignUp: boolean,
      referralCode?: string,
      invitationCode?: string,
    ) => {
      trackEvent(
        VERIFY_EVENTS.VIEW_AUTH_ENTER_OTP,
        buildOtpViewEventData(isConfirmSignUp, referralCode, invitationCode),
      );
    },
    [trackEvent],
  );

  const trackSelectAuthVerifyOTP = useCallback(
    (
      isConfirmSignUp: boolean,
      referralCode?: string,
      invitationCode?: string,
    ) => {
      trackEvent(
        VERIFY_EVENTS.SELECT_AUTH_VERIFY_OTP,
        buildOtpViewEventData(isConfirmSignUp, referralCode, invitationCode),
      );
    },
    [trackEvent],
  );

  const trackVerifyToken = useCallback(
    (
      token: string,
      referralInfoCode?: string,
      referralCode?: string,
      invitationCode?: string,
    ) => {
      const tokenDecoded = getTokenDecoded(token);
      if (!tokenDecoded) return;

      const expirationJWT = tokenDecoded.exp * SECOND;
      const now = new Date().getTime();

      const dataEvent: IEventBaseDataSchema = {
        Token: {
          expiration: {
            now: new Date(),
            date: expirationJWT,
            relative: expirationJWT - now,
          },
        },
        project_tag: PROJECT_TAGS.ACCOUNT_ACCESS,
      };
      trackEvent(VERIFY_EVENTS.VERIFY_TOKEN, dataEvent);
      const userId = tokenDecoded[CTokenDecoded.CUSTOM_USER_UUID];
      const identityData: IEventIdentifyDataDefinition = {
        id: userId,
        role: tokenDecoded[CTokenDecoded.CUSTOM_ROLES],
        invitation_code: invitationCode ?? '',
        referral_code_used: referralCode ?? '',
        referral_code_owned: referralInfoCode ?? '',
      };
      const modakGoal = localStorage.getItem(GOAL_WITH_MODAK_STORAGE_KEY);
      if (modakGoal) {
        identityData.goal_with_modak = modakGoal;
      }
      trackIdentifyEvents(userId, identityData);
    },
    [trackEvent, trackIdentifyEvents],
  );

  const trackSelectAuthResendOTP = useCallback(
    (isFromSignIn: boolean, invitationCode?: string, referralCode?: string) => {
      trackEvent(
        VERIFY_EVENTS.SELECT_AUTH_RESEND_OTP,
        buildOtpResultEventData(isFromSignIn, invitationCode, referralCode),
      );
    },
    [trackEvent],
  );

  const trackApiAuthVerifyOTPSuccess = useCallback(
    (isFromSignIn: boolean, invitationCode?: string, referralCode?: string) => {
      trackEvent(
        VERIFY_EVENTS.API_AUTH_VERIFY_OTP_SUCCESS,
        buildOtpResultEventData(isFromSignIn, invitationCode, referralCode),
      );
    },
    [trackEvent],
  );

  const trackApiAuthVerifyOTPError = useCallback(
    (err: TrackedError, origin: OriginType) => {
      trackEvent(VERIFY_EVENTS.API_AUTH_VERIFY_OTP_ERROR, {
        project_tag: PROJECT_TAGS.ACCOUNT_ACCESS,
        origin: origin,
        error: buildErrorPayload(err),
      });
    },
    [trackEvent],
  );

  const trackApiAuthVerifyOTPNoCode = useCallback(
    (origin: OriginType, shared_key?: string, username?: string) => {
      trackEvent(VERIFY_EVENTS.API_AUTH_VERIFY_OTP_NO_CODE, {
        project_tag: PROJECT_TAGS.ACCOUNT_ACCESS,
        origin: origin,
        metadata: {
          shared_key: shared_key,
          username: username,
        },
      });
    },
    [trackEvent],
  );

  const trackApiAuthResendOTPError = useCallback(
    (err: TrackedError) => {
      trackEvent(VERIFY_EVENTS.API_AUTH_RESEND_OTP_ERROR, {
        project_tag: PROJECT_TAGS.ACCOUNT_ACCESS,
        error: buildErrorPayload(err),
      });
    },
    [trackEvent],
  );

  return {
    trackViewAuthEnterOTP,
    trackSelectAuthVerifyOTP,
    trackVerifyToken,
    trackApiAuthVerifyOTPSuccess,
    trackApiAuthVerifyOTPError,
    trackSelectAuthResendOTP,
    trackApiAuthResendOTPError,
    trackApiAuthVerifyOTPNoCode,
  };
};
